import React, { useMemo } from "react";

import "./NexusSignalSummary.scss";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IconButton, Tooltip } from "@mui/material";
import moment from "moment";

import {
  ENGINE_EXPLAINER,
  INVERSE_BTC_TICKERS,
  LONG_BTC_TICKERS,
  NEXUS_STANCE_COLOR,
  NEXUS_TICKER_COLOR,
  NEXUS_TICKER_FALLBACK_COLOR,
  STANCE_HEADLINE,
  stripInlineMarkdown,
} from "./constants";

// Backend performance-summary values are decimal fractions (e.g. -0.084);
// tolerate already-in-percent values defensively.
const summaryPct = (value) => {
  if (value === null || value === undefined || isNaN(value)) return null;
  const pct = Math.abs(value) <= 1.5 ? value * 100 : value;
  return pct;
};

/**
 * Allocation Signal: the callout holds the AI rationale (wand icon stacked
 * above the copy), the stance chip, and the allocation bar + legend. Below it,
 * Current Position renders as one joined, outlined three-column container.
 *
 * Stance, headline, win rate, and signal P&L come from the authoritative
 * `signal` object on the performance-summary response (added for Navigator
 * portfolios — see docs/bitcoin-navigator-backend-gaps.md). When the signal
 * is absent (older backend, or a non-Navigator tier) it degrades gracefully:
 * stance is derived from the allocation mix, the headline falls back to the
 * static per-stance copy, and win rate / signal P&L render "—". Win rate and
 * signal P&L can also be null individually — win rate until enough episodes
 * have closed with snapshot coverage, signal P&L while the stance is CASH.
 * Rationale (latest rebalance reason) and Max Drawdown (performance-summary
 * inception period) are unchanged.
 */
const NexusSignalSummary = ({ portfolio, performanceSummary }) => {
  // Authoritative allocation signal (Navigator tier only); null on older
  // backends or other tiers, where the fields below degrade gracefully.
  const signal = performanceSummary?.signal || null;

  const allocation = useMemo(() => {
    const allocations = portfolio?.allocations || [];
    const entries = allocations.map((item) => ({
      ticker: item.ticker,
      weight: Number(item.ticker_weightage || 0) * 100,
    }));
    // The strategy parks unallocated weight in cash; surface it when the
    // backend does not return an explicit CASH row.
    const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
    if (!entries.some((entry) => entry.ticker === "CASH") && total < 99.5) {
      entries.push({ ticker: "CASH", weight: 100 - total });
    }
    return entries.sort((a, b) => b.weight - a.weight);
  }, [portfolio]);

  // Fallback stance derivation for when the backend signal is absent: any
  // inverse-ETF weight => SHORT, any other long-BTC weight => LONG, else CASH.
  const derivedStance = useMemo(() => {
    const weightOf = (tickers) =>
      allocation
        .filter((entry) => tickers.includes(entry.ticker))
        .reduce((sum, entry) => sum + entry.weight, 0);
    if (weightOf(INVERSE_BTC_TICKERS) > 0) return "SHORT";
    if (weightOf(LONG_BTC_TICKERS) > 0) return "LONG";
    return "CASH";
  }, [allocation]);

  const latestRebalance = useMemo(() => {
    const rebalances = [...(portfolio?.adjustments || [])];
    rebalances.sort(
      (a, b) =>
        new Date(b.rebalance_date).getTime() -
        new Date(a.rebalance_date).getTime(),
    );
    return rebalances[0] || null;
  }, [portfolio]);

  // Backend signal carries an authoritative EST `as_of`; fall back to the
  // latest rebalance date, then the portfolio's last-updated timestamp.
  const asOf = signal?.as_of
    ? moment(signal.as_of).format("MMM D, YYYY")
    : latestRebalance?.rebalance_date
      ? moment(latestRebalance.rebalance_date).format("MMM D, YYYY")
      : portfolio?.last_updated
        ? moment(portfolio.last_updated).format("MMM D, YYYY")
        : null;

  const maxDrawdown = useMemo(() => {
    const periods = performanceSummary?.periods || [];
    const inception =
      periods.find(
        (period) => period.period_label?.toLowerCase() === "inception",
      ) || periods[periods.length - 1];
    return summaryPct(inception?.max_drawdown);
  }, [performanceSummary]);

  if (!portfolio) return null;

  // Backend signal is authoritative; fall back to the client-side derivation.
  const stance = signal?.stance || derivedStance;
  const stanceColor = NEXUS_STANCE_COLOR[stance];
  const headline = signal?.headline || STANCE_HEADLINE[stance];
  const rationale = stripInlineMarkdown(latestRebalance?.reason || "");

  // Win Rate and Signal P&L are decimal fractions on the signal object, each
  // nullable (win rate until enough episodes close with snapshot coverage;
  // signal P&L while the stance is CASH). summaryPct returns null for missing
  // and non-numeric values alike, so a null collapses to the "—" state.
  const winRatePct = summaryPct(signal?.win_rate);
  const signalPnlPct = summaryPct(signal?.signal_pnl);
  const signalPnlSince = signal?.signal_pnl_since
    ? `Since signal opened ${moment(signal.signal_pnl_since).format("MMM D")}`
    : "Current signal";

  const metricCards = [
    winRatePct === null
      ? {
          label: "Win Rate",
          value: "—",
          sub: "Not yet available",
          tone: "neutral",
        }
      : {
          label: "Win Rate",
          value: `${winRatePct.toFixed(1)}%`,
          sub: `${signal.signals_profitable} of ${signal.signals_total} signals profitable`,
          tone: "positive",
        },
    signalPnlPct === null
      ? {
          label: "Signal P&L",
          value: "—",
          sub: stance === "CASH" ? "No open signal" : "Not yet available",
          tone: "neutral",
        }
      : {
          label: "Signal P&L",
          value: `${signalPnlPct > 0 ? "+" : ""}${signalPnlPct.toFixed(2)}%`,
          sub: signalPnlSince,
          tone: signalPnlPct >= 0 ? "positive" : "negative",
        },
    {
      label: "Max Drawdown",
      value: maxDrawdown === null ? "—" : `${maxDrawdown.toFixed(2)}%`,
      sub: maxDrawdown === null ? "Not yet available" : "Since inception",
      tone: maxDrawdown === null ? "neutral" : "negative",
    },
  ];

  return (
    <div className="nexus-signal-summary">
      <div className="nexus-signal-summary__header">
        <span className="nexus-signal-summary__title">Allocation Signal</span>
        <Tooltip title={ENGINE_EXPLAINER} arrow placement="top">
          <IconButton size="small" className="nexus-signal-summary__info">
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {asOf && (
          <span className="nexus-signal-summary__as-of">As of {asOf}</span>
        )}
      </div>

      <div className="nexus-signal-summary__callout">
        <div className="nexus-signal-summary__callout-top">
          <AutoFixHighIcon
            className="nexus-signal-summary__wand"
            sx={{ fontSize: 22 }}
          />
          <span
            className="nexus-signal-summary__stance-chip"
            style={{ backgroundColor: `${stanceColor}1f`, color: stanceColor }}
          >
            {stance}
          </span>
        </div>

        <div className="nexus-signal-summary__rationale">
          <strong>{headline}</strong>
          {rationale ? `: ${rationale}` : ""}
        </div>

        <div className="nexus-signal-summary__bar">
          {allocation
            .filter((entry) => entry.weight > 0)
            .map((entry) => (
              <div
                key={entry.ticker}
                style={{
                  width: `${entry.weight}%`,
                  backgroundColor:
                    NEXUS_TICKER_COLOR[entry.ticker] ||
                    NEXUS_TICKER_FALLBACK_COLOR,
                }}
              />
            ))}
        </div>
        <div className="nexus-signal-summary__legend">
          {allocation.map((entry) => (
            <div
              key={entry.ticker}
              className={`nexus-signal-summary__legend-item ${
                entry.weight === 0 ? "dimmed" : ""
              }`}
            >
              <span
                className="nexus-signal-summary__legend-dot"
                style={{
                  backgroundColor:
                    NEXUS_TICKER_COLOR[entry.ticker] ||
                    NEXUS_TICKER_FALLBACK_COLOR,
                }}
              />
              <span className="nexus-signal-summary__legend-ticker">
                {entry.ticker}
              </span>
              <span className="nexus-signal-summary__legend-weight">
                {entry.weight.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="nexus-signal-summary__position">
        <div className="nexus-signal-summary__subtitle">Current Position</div>
        <div className="nexus-signal-summary__stats">
          {metricCards.map((metric) => (
            <div className="nexus-signal-summary__stat" key={metric.label}>
              <span className="nexus-signal-summary__stat-label">
                {metric.label}
              </span>
              <span
                className={`nexus-signal-summary__stat-value ${metric.tone}`}
              >
                {metric.value}
              </span>
              <span className="nexus-signal-summary__stat-sub">
                {metric.sub}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NexusSignalSummary;
